/**
 * Imperative DOM utilities for rendering heading arrows on MapLibre markers.
 * No React or Emotion — these functions operate directly on HTMLElements.
 */

import { ellipseOrbit } from '../utils'

// Default orbit radii for circular waypoint markers (px) — matches overwatch RB_ORBIT_CIRCLE = 25
// Pill (ODD) markers store their wider rx in data-arrow-rx on the outer div.
const ORBIT_RX_DEFAULT = 25
const ORBIT_RY_DEFAULT = 25

// Arrow canvas size (px, logical) — matches overwatch ARROW_W=14, ARROW_H=10
const ARROW_W = 14
const ARROW_H = 10

// Data attribute used to identify arrow wrapper elements
const ARROW_WRAPPER_ATTR = 'data-heading-arrows'

/** Inject the pulse keyframe once per document. */
const ensurePulseKeyframes = (): void => {
  if (document.getElementById('rb-heading-arrow-pulse')) return
  const style = document.createElement('style')
  style.id = 'rb-heading-arrow-pulse'
  style.textContent = `
    @keyframes rb-heading-arrow-pulse {
      0%, 100% { transform: var(--rb-arrow-rotate) scale(1); }
      50% { transform: var(--rb-arrow-rotate) scale(1.4); }
    }
  `
  document.head.appendChild(style)
}

// Cache arrow ImageData per stroke color to avoid redundant canvas draws.
const arrowImageDataCache = new Map<string, ImageData>()

const createArrowCanvas = (strokeColor: string): HTMLCanvasElement => {
  const scale = window.devicePixelRatio || 2
  const canvas = document.createElement('canvas')
  canvas.width = ARROW_W * scale
  canvas.height = ARROW_H * scale
  canvas.style.width = `${ARROW_W}px`
  canvas.style.height = `${ARROW_H}px`
  canvas.style.display = 'block'

  const ctx = canvas.getContext('2d')
  if (ctx) {
    const cached = arrowImageDataCache.get(strokeColor)
    if (cached) {
      ctx.putImageData(cached, 0, 0)
    } else {
      ctx.scale(scale, scale)
      ctx.fillStyle = '#FFFFFF'
      ctx.strokeStyle = strokeColor
      ctx.lineWidth = 1.5
      ctx.lineJoin = 'round'
      ctx.beginPath()
      ctx.moveTo(ARROW_W / 2, 1) // tip
      ctx.lineTo(ARROW_W - 1, ARROW_H - 1) // bottom right
      ctx.lineTo(1, ARROW_H - 1) // bottom left
      ctx.closePath()
      ctx.fill()
      ctx.stroke()
      arrowImageDataCache.set(
        strokeColor,
        ctx.getImageData(0, 0, canvas.width, canvas.height),
      )
    }
  }
  return canvas
}

const createArrowElement = (
  strokeColor: string,
  bearing: number,
  dx: number,
  dy: number,
): HTMLDivElement => {
  const el = document.createElement('div')
  Object.assign(el.style, {
    position: 'absolute',
    left: `calc(50% + ${dx - ARROW_W / 2}px)`,
    top: `calc(50% + ${dy - ARROW_H / 2}px)`,
    width: `${ARROW_W}px`,
    height: `${ARROW_H}px`,
    zIndex: '200',
    transform: `rotate(${bearing}deg)`,
  })
  el.appendChild(createArrowCanvas(strokeColor))
  return el
}

/**
 * Append pulsing selection arrows around a marker element.
 * Each arrow calls onHeadingSelect with its bearing when clicked.
 * Reads orbit radii from data-arrow-rx / data-arrow-ry on the outer div
 * so pill (ODD) markers automatically use their wider orbit.
 */
export const showHeadingSelectionArrows = (
  markerEl: HTMLElement,
  options: {
    strokeColor: string
    availableDirections: number[] // bearings to show; defaults to cardinal
    onHeadingSelect: (bearing: number) => void
    hasExistingHeading: boolean
    onRemoveHeading?: () => void
  },
): void => {
  removeHeadingArrowsFromMarker(markerEl)
  ensurePulseKeyframes()

  const inner = markerEl.querySelector('div') as HTMLDivElement | null
  if (!inner) return
  inner.style.position = 'relative'
  inner.style.overflow = 'visible'

  const rx = parseFloat(markerEl.dataset.arrowRx ?? String(ORBIT_RX_DEFAULT))
  const ry = parseFloat(markerEl.dataset.arrowRy ?? String(ORBIT_RY_DEFAULT))

  const wrapper = document.createElement('div')
  wrapper.setAttribute(ARROW_WRAPPER_ATTR, '1')
  wrapper.style.position = 'absolute'
  wrapper.style.inset = '0'
  wrapper.style.overflow = 'visible'
  wrapper.style.pointerEvents = 'none'

  const bearings = options.availableDirections

  for (const bearing of bearings) {
    const { dx, dy } = ellipseOrbit(rx, ry, bearing)
    const btn = createArrowElement(options.strokeColor, bearing, dx, dy)
    btn.style.cursor = 'pointer'
    btn.style.pointerEvents = 'auto'
    btn.style.setProperty('--rb-arrow-rotate', `rotate(${bearing}deg)`)
    btn.style.animation = 'rb-heading-arrow-pulse 1.2s ease-in-out infinite'

    btn.addEventListener('mouseenter', () => {
      btn.style.animation = 'none'
      btn.style.transform = `rotate(${bearing}deg) scale(1.5)`
    })
    btn.addEventListener('mouseleave', () => {
      btn.style.setProperty('--rb-arrow-rotate', `rotate(${bearing}deg)`)
      btn.style.animation = 'rb-heading-arrow-pulse 1.2s ease-in-out infinite'
    })
    btn.addEventListener('click', e => {
      e.stopPropagation()
      e.preventDefault()
      options.onHeadingSelect(bearing)
    })

    wrapper.appendChild(btn)
  }

  if (options.hasExistingHeading && options.onRemoveHeading) {
    const actionsRow = document.createElement('div')
    Object.assign(actionsRow.style, {
      position: 'absolute',
      left: '50%',
      top: `calc(50% + ${ry + ARROW_H / 2 + 8}px)`,
      transform: 'translateX(-50%)',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      zIndex: '300',
      pointerEvents: 'auto',
    })

    const removeBtn = document.createElement('div')
    Object.assign(removeBtn.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '5px 10px',
      background: 'white',
      border: '1px solid #ccc',
      borderRadius: '6px',
      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      fontFamily: 'sans-serif',
      fontSize: '12px',
      color: '#333',
    })
    removeBtn.textContent = 'Remove heading'
    removeBtn.addEventListener('mouseenter', () => {
      removeBtn.style.background = '#f5f5f5'
    })
    removeBtn.addEventListener('mouseleave', () => {
      removeBtn.style.background = 'white'
    })
    removeBtn.addEventListener('click', e => {
      e.stopPropagation()
      e.preventDefault()
      options.onRemoveHeading?.()
    })

    actionsRow.appendChild(removeBtn)
    wrapper.appendChild(actionsRow)
  }

  inner.appendChild(wrapper)
}

/**
 * Render a static confirmed heading indicator arrow (no selection interaction).
 * Reads orbit radii from data-arrow-rx / data-arrow-ry for pill markers.
 */
export const addConfirmedHeadingArrow = (
  markerEl: HTMLElement,
  heading: number,
  strokeColor: string,
): void => {
  removeHeadingArrowsFromMarker(markerEl)

  const inner = markerEl.querySelector('div') as HTMLDivElement | null
  if (!inner) return
  inner.style.position = 'relative'
  inner.style.overflow = 'visible'

  const rx = parseFloat(markerEl.dataset.arrowRx ?? String(ORBIT_RX_DEFAULT))
  const ry = parseFloat(markerEl.dataset.arrowRy ?? String(ORBIT_RY_DEFAULT))

  const wrapper = document.createElement('div')
  wrapper.setAttribute(ARROW_WRAPPER_ATTR, '1')
  wrapper.style.position = 'absolute'
  wrapper.style.inset = '0'
  wrapper.style.overflow = 'visible'
  wrapper.style.pointerEvents = 'none'

  const { dx, dy } = ellipseOrbit(rx, ry, heading)
  const arrow = createArrowElement(strokeColor, heading, dx, dy)
  wrapper.appendChild(arrow)
  inner.appendChild(wrapper)
}

/** Remove all heading arrow wrappers from a marker element. */
export const removeHeadingArrowsFromMarker = (markerEl: HTMLElement): void => {
  const inner = markerEl.querySelector('div')
  if (!inner) return
  for (const el of Array.from(
    inner.querySelectorAll(`[${ARROW_WRAPPER_ATTR}]`),
  )) {
    el.remove()
  }
}
